// /manufacturing-orders — список заказов на производство.
//
// Заказ на производство — офисный документ «сделать N изделий артикула X»
// (не путать с /orders — заданиями на стадию, которые он порождает при
// запуске).  Колонки атрибутов — те, что описания заказа техпроцессов
// помечают list_column.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { StatusPill } from "@/components/StatusPill";
import { useAuth } from "@/lib/AuthContext";
import { ApiError, manufacturingOrdersApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { localized } from "@/lib/localized";
import { ManufacturingOrderStatus, type ManufacturingOrderSource, type OrderAttributeDef } from "@/lib/types";

const SELECT_CLS = "mt-1 px-3 py-2 rounded-md bg-surface border border-border text-sm";

export default function ManufacturingOrdersList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("manufacturing_order.create");
  const canDelete = hasPermission("manufacturing_order.delete");

  const processes = useQuery({
    queryKey: ["manufacturing-orders", "processes"],
    queryFn: () => manufacturingOrdersApi.processes(),
    staleTime: 60_000,
  });
  const processList = processes.data ?? [];
  const processName = (key: string | null) => processList.find((p) => p.key === key)?.name ?? key ?? "—";

  // Колонки: атрибуты list_column всех техпроцессов (первое описание ключа).
  const columns: OrderAttributeDef[] = [];
  for (const p of processList) {
    for (const d of p.order.attributes) {
      if (d.list_column && !columns.some((c) => c.key === d.key)) columns.push(d);
    }
  }

  const del = useMutation({
    mutationFn: (id: string) => manufacturingOrdersApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["manufacturing-orders"] }),
    onError: (e) =>
      window.alert(
        e instanceof ApiError && e.code === "CONFLICT"
          ? t("mo.detail.deleteConflict")
          : e instanceof ApiError
            ? `${e.code}: ${e.message}`
            : e instanceof Error
              ? e.message
              : t("mo.detail.deleteFailed"),
      ),
  });

  const [params, setParams] = useSearchParams();
  const statusFilter = (params.get("status") as (typeof ManufacturingOrderStatus)[number] | null) || undefined;
  const articleFilter = params.get("article") || undefined;
  const sourceFilter = (params.get("source") as ManufacturingOrderSource | null) || undefined;
  const processFilter = params.get("process") || undefined;

  const orders = useQuery({
    queryKey: ["manufacturing-orders", "list", statusFilter, articleFilter, sourceFilter, processFilter],
    queryFn: () =>
      manufacturingOrdersApi.list({
        page_size: 100,
        status: statusFilter,
        article: articleFilter,
        source_system: sourceFilter,
        process_key: processFilter,
      }),
  });

  function setFilter(key: string, value: string | undefined) {
    const next = new URLSearchParams(params);
    if (value === undefined || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  const hasFilters = !!(statusFilter || articleFilter || sourceFilter || processFilter);

  return (
    <RequirePermission codes="manufacturing_order.read">
      <PageHeader
        eyebrow={t("mo.eyebrow")}
        title={t("mo.list.title")}
        description={t("mo.list.description")}
        actions={
          canCreate ? (
            <div className="flex items-center gap-2">
              {processList.map((p, i) => (
                <button
                  key={p.key}
                  className={i === 0 ? "btn btn-primary" : "btn"}
                  onClick={() => navigate(`/manufacturing-orders/new?process=${encodeURIComponent(p.key)}`)}
                  title={p.name}
                >
                  + {processList.length > 1 ? p.name : t("mo.list.newOrder")}
                </button>
              ))}
            </div>
          ) : undefined
        }
      />

      <section className="card p-4 flex flex-wrap items-end gap-4">
        <label className="block text-sm">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("mo.detail.colStatus")}</span>
          <select
            value={statusFilter ?? ""}
            onChange={(e) => setFilter("status", e.target.value || undefined)}
            className={SELECT_CLS}
          >
            <option value="">{t("mo.list.all")}</option>
            {ManufacturingOrderStatus.map((s) => (
              <option key={s} value={s}>
                {t(`mo.status.${s}`, s)}
              </option>
            ))}
          </select>
        </label>

        {processList.length > 1 && (
          <label className="block text-sm">
            <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("mo.form.process")}</span>
            <select
              value={processFilter ?? ""}
              onChange={(e) => setFilter("process", e.target.value || undefined)}
              className={SELECT_CLS}
            >
              <option value="">{t("mo.list.all")}</option>
              {processList.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="block text-sm">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("mo.list.source")}</span>
          <select
            value={sourceFilter ?? ""}
            onChange={(e) => setFilter("source", e.target.value || undefined)}
            className={SELECT_CLS}
          >
            <option value="">{t("mo.list.all")}</option>
            <option value="mes">{t("mo.source.manual")}</option>
            <option value="staiter">{t("mo.source.external")}</option>
          </select>
        </label>

        <label className="block text-sm flex-1 min-w-[12rem]">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("mo.fields.article")}</span>
          <input
            value={articleFilter ?? ""}
            onChange={(e) => setFilter("article", e.target.value || undefined)}
            className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border text-sm font-mono"
          />
        </label>

        {hasFilters && (
          <button className="btn text-xs" onClick={() => setParams({}, { replace: true })}>
            {t("mo.list.resetFilters")}
          </button>
        )}
      </section>

      {orders.isLoading ? (
        <LoadingState />
      ) : orders.isError ? (
        <ErrorState error={orders.error} />
      ) : orders.data!.items.length === 0 ? (
        <EmptyState title={t("mo.list.empty")} hint={canCreate ? t("mo.list.emptyCreate") : t("mo.list.emptyWait")} />
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="px-4 py-3">{t("mo.list.colNumber")}</th>
                {processList.length > 1 && <th className="px-4 py-3">{t("mo.form.process")}</th>}
                <th className="px-4 py-3">{t("mo.fields.article")}</th>
                <th className="px-4 py-3 text-right">{t("mo.fields.elementCount")}</th>
                {columns.map((c) => (
                  <th key={c.key} className="px-4 py-3">
                    {localized(c.label, c.key)}
                  </th>
                ))}
                <th className="px-4 py-3">{t("mo.detail.colStatus")}</th>
                <th className="px-4 py-3">{t("mo.list.source")}</th>
                <th className="px-4 py-3">{t("mo.detail.created")}</th>
                {canDelete && <th className="px-4 py-3 text-right" />}
              </tr>
            </thead>
            <tbody>
              {orders.data!.items.map((o) => (
                <tr key={o.id} className="border-t border-border hover:bg-surface-hover">
                  <td className="px-4 py-3">
                    <Link to={`/manufacturing-orders/${o.id}`} className="font-mono text-accent-strong hover:underline">
                      {o.code}
                    </Link>
                  </td>
                  {processList.length > 1 && (
                    <td className="px-4 py-3">
                      <span className="pill">{processName(o.process_key)}</span>
                    </td>
                  )}
                  <td className="px-4 py-3 font-mono">{o.article ?? "—"}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{o.element_count ?? "—"}</td>
                  {columns.map((c) => {
                    const v = o.attributes[c.key];
                    return (
                      <td key={c.key} className="px-4 py-3 tabular-nums text-text-secondary">
                        {v == null || v === "" ? "—" : typeof v === "boolean" ? (v ? t("common.yes") : t("common.no")) : String(v)}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3">
                    <StatusPill kind="manufacturing" value={o.status} />
                  </td>
                  <td className="px-4 py-3">
                    <span className="pill">
                      {o.source_system === "staiter" ? t("mo.source.external") : t("mo.source.manual")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-text-muted font-mono text-xs">{formatDateTime(o.created_at)}</td>
                  {canDelete && (
                    <td className="px-4 py-3 text-right">
                      <button
                        className="btn btn-danger text-xs"
                        onClick={() => {
                          if (window.confirm(t("mo.detail.deleteConfirm", { code: o.code }))) del.mutate(o.id);
                        }}
                        disabled={del.isPending}
                      >
                        {t("common.delete")}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </RequirePermission>
  );
}
